// Kategoriye özel akıllı hesaplama tabloları (Mimarobot tarzı).
// Her hesaplayıcı: alan listesi (fields) + canlı hesap (compute).
// compute(values) => { outputs: [{label, value, unit, apply?}], quantity, unit, note }
// "apply: true" olan çıktılar tek tıkla kalemin miktar/birimine aktarılabilir.

const r2 = (n) => Math.round(n * 100) / 100;

const DOOR_M2 = 1.8; // standart kapı boşluğu ~0,90×2,00 m
const WINDOW_M2 = 1.44; // standart pencere ~1,20×1,20 m

export const CALCULATORS = {
  paint: {
    title: "Boya Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Mekan Eni (m)", def: 0 },
      { key: "length", label: "Mekan Boyu (m)", def: 0 },
      { key: "height", label: "Tavan Yüksekliği (m)", def: 2.7 },
      { key: "doors", label: "Kapı Sayısı (adet)", def: 1 },
      { key: "windows", label: "Pencere Sayısı (adet)", def: 1 },
      { key: "coats", label: "Kat Sayısı", def: 2 },
      { key: "ceiling", label: "Tavan da Boyanacak", type: "bool", def: false },
    ],
    compute: (v) => {
      const wall = Math.max(
        0,
        2 * (v.width + v.length) * v.height - v.doors * DOOR_M2 - v.windows * WINDOW_M2
      );
      const ceil = v.ceiling ? v.width * v.length : 0;
      const area = wall + ceil;
      if (area <= 0) return null;
      const coats = v.coats || 1;
      const paintL = Math.ceil((area * coats) / 10);
      const primerL = Math.ceil(area / 12);
      return {
        outputs: [
          { label: "Net duvar alanı", value: r2(wall), unit: "m²" },
          ...(v.ceiling ? [{ label: "Tavan alanı", value: r2(ceil), unit: "m²" }] : []),
          { label: "Boya ihtiyacı", value: paintL, unit: "lt", apply: true },
          { label: "Astar ihtiyacı", value: primerL, unit: "lt", apply: true },
        ],
        quantity: r2(area),
        unit: "m²",
        note: "Kapı 1,8 m² / pencere 1,44 m² düşülür · 1 litre boya ≈ 10 m² (tek kat)",
      };
    },
  },

  flooring: {
    title: "Zemin Kaplama Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Mekan Eni (m)", def: 0 },
      { key: "length", label: "Mekan Boyu (m)", def: 0 },
      { key: "waste", label: "Fire Payı (%)", def: 8 },
      { key: "doors", label: "Kapı Sayısı (adet)", def: 1 },
    ],
    compute: (v) => {
      const area = v.width * v.length;
      if (area <= 0) return null;
      const material = area * (1 + (v.waste || 0) / 100);
      const skirting = Math.max(0, 2 * (v.width + v.length) - v.doors * 0.9);
      return {
        outputs: [
          { label: "Zemin alanı", value: r2(area), unit: "m²" },
          { label: "Fire dahil malzeme", value: r2(material), unit: "m²", apply: true },
          { label: "Süpürgelik ihtiyacı", value: r2(skirting), unit: "mtül", apply: true },
        ],
        quantity: r2(material),
        unit: "m²",
        note: "Süpürgelik = çevre − kapı boşlukları (0,9 m/kapı)",
      };
    },
  },

  wall_tile: {
    title: "Duvar Seramiği Hesaplama Tablosu",
    fields: [
      { key: "wallLength", label: "Duvar Uzunluğu Toplam (m)", def: 0 },
      { key: "height", label: "Kaplama Yüksekliği (m)", def: 2.4 },
      { key: "doors", label: "Kapı Sayısı (adet)", def: 1 },
      { key: "windows", label: "Pencere Sayısı (adet)", def: 0 },
      { key: "waste", label: "Fire Payı (%)", def: 10 },
    ],
    compute: (v) => {
      const area = Math.max(
        0,
        v.wallLength * v.height - v.doors * DOOR_M2 - v.windows * WINDOW_M2
      );
      if (area <= 0) return null;
      const material = area * (1 + (v.waste || 0) / 100);
      return {
        outputs: [
          { label: "Net duvar alanı", value: r2(area), unit: "m²" },
          { label: "Fire dahil malzeme", value: r2(material), unit: "m²", apply: true },
        ],
        quantity: r2(material),
        unit: "m²",
        note: "Birden fazla duvar için uzunlukları toplayın",
      };
    },
  },

  plaster: {
    title: "Alçı Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Mekan Eni (m)", def: 0 },
      { key: "length", label: "Mekan Boyu (m)", def: 0 },
      { key: "height", label: "Tavan Yüksekliği (m)", def: 2.7 },
      { key: "doors", label: "Kapı Sayısı (adet)", def: 1 },
      { key: "windows", label: "Pencere Sayısı (adet)", def: 1 },
      { key: "kgPerM2", label: "Sarfiyat (kg/m²)", def: 8 },
    ],
    compute: (v) => {
      const area =
        v.length > 0
          ? Math.max(
              0,
              2 * (v.width + v.length) * v.height - v.doors * DOOR_M2 - v.windows * WINDOW_M2
            )
          : Math.max(0, v.width * v.height);
      if (area <= 0) return null;
      const kg = area * (v.kgPerM2 || 0);
      const bags = Math.ceil(kg / 30);
      return {
        outputs: [
          { label: "Net duvar alanı", value: r2(area), unit: "m²" },
          { label: "Toplam alçı", value: r2(kg), unit: "kg" },
          { label: "Torba ihtiyacı (30 kg)", value: bags, unit: "torba", apply: true },
        ],
        quantity: r2(area),
        unit: "m²",
        note: "Alçı sıva ~8-10 kg/m² · saten alçı ~1,5 kg/m² (sarfiyatı değiştirin)",
      };
    },
  },

  ceiling: {
    title: "Asma Tavan / Alçıpan Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Mekan Eni (m)", def: 0 },
      { key: "length", label: "Mekan Boyu (m)", def: 0 },
      { key: "waste", label: "Fire Payı (%)", def: 10 },
    ],
    compute: (v) => {
      const area = v.width * v.length;
      if (area <= 0) return null;
      const material = area * (1 + (v.waste || 0) / 100);
      const plates = Math.ceil(material / 2.88);
      return {
        outputs: [
          { label: "Tavan alanı", value: r2(area), unit: "m²" },
          { label: "Fire dahil malzeme", value: r2(material), unit: "m²", apply: true },
          { label: "Alçıpan plaka (120×240)", value: plates, unit: "adet", apply: true },
        ],
        quantity: r2(material),
        unit: "m²",
        note: "1 plaka = 2,88 m² · profil ve askı malzemesi ayrıca hesaplanmalı",
      };
    },
  },

  screed: {
    title: "Şap Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Mekan Eni (m)", def: 0 },
      { key: "length", label: "Mekan Boyu (m)", def: 0 },
      { key: "thickness", label: "Şap Kalınlığı (cm)", def: 5 },
    ],
    compute: (v) => {
      const area = v.width * v.length;
      if (area <= 0) return null;
      const volume = (area * (v.thickness || 0)) / 100;
      const cementBags = Math.ceil(volume * 7);
      const sand = r2(volume * 1.1);
      return {
        outputs: [
          { label: "Zemin alanı", value: r2(area), unit: "m²" },
          { label: "Şap hacmi", value: r2(volume), unit: "m³" },
          { label: "Çimento (50 kg)", value: cementBags, unit: "torba", apply: true },
          { label: "Kum ihtiyacı", value: sand, unit: "m³" },
        ],
        quantity: r2(area),
        unit: "m²",
        note: "1 m³ şap ≈ 350 kg çimento (7 torba) + ~1,1 m³ kum",
      };
    },
  },

  roof: {
    title: "Çatı Hesaplama Tablosu",
    fields: [
      { key: "width", label: "Çatı Eni (m)", def: 0 },
      { key: "length", label: "Çatı Boyu (m)", def: 0 },
      { key: "waste", label: "Fire Payı (%)", def: 5 },
    ],
    compute: (v) => {
      const area = v.width * v.length;
      if (area <= 0) return null;
      const material = area * (1 + (v.waste || 0) / 100);
      const gutter = 2 * (v.width + v.length);
      return {
        outputs: [
          { label: "Çatı alanı", value: r2(area), unit: "m²" },
          { label: "Fire dahil panel/sac", value: r2(material), unit: "m²", apply: true },
          { label: "Oluk metrajı (çevre)", value: r2(gutter), unit: "mtül", apply: true },
        ],
        quantity: r2(material),
        unit: "m²",
        note: "Panel/sac = en × boy (fire dahil) · oluk = çatı çevresi 2×(en+boy)",
      };
    },
  },

  insulation: {
    title: "Mantolama Hesaplama Tablosu",
    fields: [
      { key: "facadeLength", label: "Cephe Uzunluğu Toplam (m)", def: 0 },
      { key: "height", label: "Bina / Cephe Yüksekliği (m)", def: 0 },
      { key: "openingArea", label: "Pencere+Kapı Alanı Toplam (m²)", def: 0 },
      { key: "waste", label: "Fire Payı (%)", def: 8 },
    ],
    compute: (v) => {
      const net = Math.max(0, v.facadeLength * v.height - (v.openingArea || 0));
      if (net <= 0) return null;
      const material = net * (1 + (v.waste || 0) / 100);
      const dowels = Math.ceil(net * 6);
      return {
        outputs: [
          { label: "Net cephe alanı", value: r2(net), unit: "m²" },
          { label: "Fire dahil levha", value: r2(material), unit: "m²", apply: true },
          { label: "Dübel ihtiyacı (6 ad/m²)", value: dowels, unit: "adet" },
        ],
        quantity: r2(net),
        unit: "m²",
        note: "Sıva filesi, köşe profili ve yapıştırıcı ayrıca hesaplanmalı",
      };
    },
  },
};

// Kategori (Ana + Alt) → hesaplayıcı eşlemesi. "subs" verilmezse ana kategorinin
// tüm alt başlıklarında geçerlidir. "defaults" alan varsayılanlarını ezebilir.
const CALC_MAP = [
  { calc: "paint", main: "Boya" },
  { calc: "flooring", main: "Parke" },
  {
    calc: "flooring",
    main: "Seramik",
    subs: ["Yer Seramiği", "Granit Seramik"],
    defaults: { waste: 10 },
  },
  {
    calc: "wall_tile",
    main: "Seramik",
    subs: ["Duvar Seramiği", "Banyo Seramiği", "Mutfak Seramiği (Tezgah Arası)"],
  },
  { calc: "plaster", main: "Alçı", subs: ["Alçı Sıva"] },
  { calc: "plaster", main: "Alçı", subs: ["Saten Alçı"], defaults: { kgPerM2: 1.5 } },
  {
    calc: "ceiling",
    main: "Alçı",
    subs: ["Asma Tavan (Alçıpan)", "Karopan", "Alçıpan Duvar"],
  },
  { calc: "screed", main: "Şap / İzolasyon", subs: ["Şap"] },
  { calc: "insulation", main: "Şap / İzolasyon", subs: ["Isı Yalıtımı (Mantolama)"] },
  { calc: "roof", main: "Panel Döşeme" },
  { calc: "roof", main: "Trapez Sac Döşeme" },
  { calc: "roof", main: "Çatı Kaplama" },
  { calc: "roof", main: "Çatı İzolasyonu" },
  { calc: "roof", main: "Oluk Yapımı" },
  { calc: "roof", main: "Dere İzolasyonu" },
];

/**
 * Seçili kategoriye uygun hesaplayıcı tanımını döner (yoksa null).
 * Dönen objede alan varsayılanları (varsa) eşlemedeki "defaults" ile ezilmiştir.
 */
export const getCalculator = (main, sub) => {
  if (!main) return null;
  const entry = CALC_MAP.find(
    (e) => e.main === main && (!e.subs || (sub && e.subs.includes(sub)))
  );
  if (!entry) return null;
  const base = CALCULATORS[entry.calc];
  if (!entry.defaults) return base;
  return {
    ...base,
    fields: base.fields.map((f) =>
      entry.defaults[f.key] !== undefined ? { ...f, def: entry.defaults[f.key] } : f
    ),
  };
};
